import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { IconHighlight, Icon, Button, Tooltip, Typography } from '@ann/web-components';
import {
  ACTIVITY_TYPE_CONFIG,
  ALERT_TYPES,
  METADATA_ICONS,
  METADATA_BAR_CONFIG,
  ACTIVITY_CARD_MAX_CHARACTERS,
} from '../../constants';
import {
  ItemContainer,
  Row,
  LeftSection,
  RightSection,
  TitleWrapper,
  DescriptionRow,
  Author,
  DescriptionWrapper,
  MetadataBar,
  MetadataItem,
  MetadataIconItem,
  IconWrapper,
} from './ActivityListItem.styled';

/**
 * Single activity list item. Expanded shows description + metadata on hover (CSS-only).
 * Compressed shows only icon, title, alert (if any), time ago, chevron.
 */
const ActivityListItem = ({
  item,
  isCompressed,
  onItemClick,
}) => {
  const typeConfig = ACTIVITY_TYPE_CONFIG[item.type] ?? ACTIVITY_TYPE_CONFIG.call;
  const hasAlerts = item.alerts?.length > 0;
  const alertTooltip = useMemo(
    () => (hasAlerts ? item.alerts.map((a) => a.message).join('\n') : ''),
    [hasAlerts, item.alerts],
  );

  const metadataKeys = METADATA_BAR_CONFIG[item.type] ?? METADATA_BAR_CONFIG.call;

  const visibleMetadataItems = useMemo(() => {
    const m = item.metadata;
    return metadataKeys.filter((key) => {
      const val = m?.[key];
      if (key === 'userId') return !!val;
      if (key === 'sentiment') return val != null;
      if (key === 'assignedTasks') return val > 0;
      if (key === 'hasRecording' || key === 'hasExternalLink' || key === 'flagged') return !!val;
      if (key === 'direction') return !!val && item.type === 'call';
      if (key === 'duration') return !!val;
      if (key === 'commentsCount' || key === 'attachmentsCount' || key === 'linkCount') return val > 0;
      return false;
    });
  }, [item.metadata, item.type, metadataKeys]);

  const hasMetadata = visibleMetadataItems.length > 0;

  const handleClick = useCallback(() => {
    onItemClick?.(item);
  }, [item, onItemClick]);

  return (
    <ItemContainer
      data-compressed={isCompressed || undefined}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <Row>
        <LeftSection>
          <IconHighlight
            icon={typeConfig.icon}
            bgColor={typeConfig.bgColor}
            iconColor={typeConfig.iconColor}
            size="small"
          />
          <TitleWrapper>
            <Typography
              category="body"
              size="medium"
              weight="medium"
              color="title"
              maxCharacters={ACTIVITY_CARD_MAX_CHARACTERS}
            >
              {item.title}
            </Typography>
          </TitleWrapper>
        </LeftSection>
        <RightSection>
          {hasAlerts && (
            <Tooltip content={alertTooltip} position="bottom" arrow={false}>
              <IconWrapper>
                <Icon
                  name={item.alerts.some((a) => a.type === ALERT_TYPES.EXPIRED) ? 'error' : 'warning'}
                  size="small"
                />
              </IconWrapper>
            </Tooltip>
          )}
          <MetadataItem>{item.timeAgo}</MetadataItem>
          <Button
            type="tertiary"
            size="small"
            iconLeft="chevronRight"
            aria-label="Ver detalles"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          />
        </RightSection>
      </Row>

      {!isCompressed && (item.description || item.author) && (
        <DescriptionRow>
          {item.author && <Author>{item.author}</Author>}
          {item.description && (
            <DescriptionWrapper>
              <Typography
                category="body"
                size="small"
                weight="regular"
                color="body"
                maxCharacters={ACTIVITY_CARD_MAX_CHARACTERS}
              >
                {item.description}
              </Typography>
            </DescriptionWrapper>
          )}
        </DescriptionRow>
      )}

      {!isCompressed && hasMetadata && (
        <MetadataBar>
          {visibleMetadataItems.map((key) => (
            <MetadataIconItem key={key}>
              {key === 'userId' && <MetadataItem>{item.metadata.userId}</MetadataItem>}
              {key === 'sentiment' && <Icon materialName={METADATA_ICONS.sentiment} size="small" />}
              {key === 'assignedTasks' && (
                <>
                  <Icon materialName={METADATA_ICONS.assignedTasks} size="small" />
                  <MetadataItem>{item.metadata.assignedTasks}</MetadataItem>
                </>
              )}
              {key === 'hasRecording' && <Icon materialName={METADATA_ICONS.hasRecording} size="small" />}
              {key === 'hasExternalLink' && <Icon materialName={METADATA_ICONS.hasExternalLink} size="small" />}
              {key === 'duration' && (
                <>
                  <Icon materialName={METADATA_ICONS.duration} size="small" />
                  <MetadataItem>{item.metadata.duration}</MetadataItem>
                </>
              )}
              {key === 'direction' && <Icon materialName={METADATA_ICONS.direction} size="small" />}
              {key === 'flagged' && <Icon materialName={METADATA_ICONS.flagged} size="small" />}
              {key === 'commentsCount' && (
                <>
                  <Icon materialName={METADATA_ICONS.comments} size="small" />
                  <MetadataItem>{item.metadata.commentsCount}</MetadataItem>
                </>
              )}
              {key === 'attachmentsCount' && (
                <>
                  <Icon materialName={METADATA_ICONS.attachments} size="small" />
                  <MetadataItem>{item.metadata.attachmentsCount}</MetadataItem>
                </>
              )}
              {key === 'linkCount' && (
                <>
                  <Icon materialName={METADATA_ICONS.link} size="small" />
                  <MetadataItem>{item.metadata.linkCount}</MetadataItem>
                </>
              )}
            </MetadataIconItem>
          ))}
        </MetadataBar>
      )}
    </ItemContainer>
  );
};

ActivityListItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    author: PropTypes.string,
    timeAgo: PropTypes.string,
    alerts: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        message: PropTypes.string,
      }),
    ),
    metadata: PropTypes.object,
  }).isRequired,
  isCompressed: PropTypes.bool,
  onItemClick: PropTypes.func,
};

ActivityListItem.defaultProps = {
  isCompressed: false,
  onItemClick: undefined,
};

export default React.memo(ActivityListItem);
